'use client';

import React, { useEffect } from 'react';
import { useForumType } from '@/components/hooks/useForumType';
import type { ForumTypeString } from '@/lib/instanceSettings';
import { AnalyticsContext } from "../../lib/analyticsEvents";
import { getReviewPhase, reviewIsActive, REVIEW_YEAR } from '../../lib/reviewUtils';
import { showReviewOnFrontPageIfActive, ultraFeedEnabledSetting } from '@/lib/instanceSettings';
import { useCookiesWithConsent } from '../hooks/useCookiesWithConsent';
import { LAST_VISITED_FRONTPAGE_COOKIE } from '../../lib/cookies/cookies';
import moment from 'moment';
import { visitorGetsDynamicFrontpage } from '../../lib/betas';
import { combineUrls, getSiteUrl } from "../../lib/vulcan-lib/utils";
import { registerComponent } from "../../lib/vulcan-lib/components";
import AnalyticsInViewTracker from "./AnalyticsInViewTracker";
import FrontpageReviewWidget from "../review/FrontpageReviewWidget";
import SingleColumnSection from "./SingleColumnSection";
import { DismissibleSpotlightItemSuspense } from "@/components/spotlights/DismissibleSpotlightItem";
import QuickTakesSection from "../quickTakes/QuickTakesSection";
import LWHomePosts from "./LWHomePosts";
import UltraFeed from "../ultraFeed/UltraFeed";
import { StructuredData } from './StructuredData';
import { SuspenseWrapper } from './SuspenseWrapper';
import DeferRender from './DeferRender';
import { defineStyles, useStyles } from '../hooks/useStyles';
import ErrorBoundary from './ErrorBoundary';
import UltraFeedErrorFallback from '../ultraFeed/UltraFeedErrorFallback';
import { SideItemsContainer, SideItemsSidebar } from '../contents/SideItems';
import { RIGHT_COLUMN_WIDTH_WITH_SIDENOTES, RIGHT_COLUMN_WIDTH_WITHOUT_SIDENOTES, RIGHT_COLUMN_WIDTH_XS, sidenotesHiddenBreakpoint } from '../posts/PostsPage/constants';

import dynamic from 'next/dynamic';
import { IsReturningVisitorContextProvider } from '@/components/layout/IsReturningVisitorContextProvider';
import { INKHAVEN_RESIDENCY_3_END, INKHAVEN_RESIDENCY_3_SPOTLIGHT_ID, INKHAVEN_RESIDENCY_3_START } from '../seasonal/Inkhaven2026Banner';
import PetrovStoryMobileBanner from '../seasonal/petrovDay/petrov-day-story/PetrovStoryMobileBanner';
import { useIsPetrovDayRitualActive } from '../seasonal/petrovDay/petrov-day-story/useIsPetrovDayRitualActive';
const RecentDiscussionFeed = dynamic(() => import("../recentDiscussion/RecentDiscussionFeed"), { ssr: false });

const styles = defineStyles("LWHome", (theme: ThemeType) => ({
  feedSections: {
    position: "relative",
  },
  sideItems: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: "100%",
    width: RIGHT_COLUMN_WIDTH_WITH_SIDENOTES,
    [sidenotesHiddenBreakpoint(theme)]: {
      width: RIGHT_COLUMN_WIDTH_WITHOUT_SIDENOTES,
    },
    [theme.breakpoints.down('xs')]: {
      width: RIGHT_COLUMN_WIDTH_XS,
    },
  },
  desktopSpotlight: {
    ['@media(max-width: 1199.95px)']: {
      display: "none",
    },
  },
  mobileSpotlight: {
    ['@media(min-width: 1200px)']: {
      display: "none",
    },
  },
}));

const getMobileSpotlightOverrideId = (now: Date = new Date()): string | null => {
  return now >= INKHAVEN_RESIDENCY_3_START && now < INKHAVEN_RESIDENCY_3_END
    ? INKHAVEN_RESIDENCY_3_SPOTLIGHT_ID
    : null;
};

const getStructuredData = (forumType: ForumTypeString) => ({
  "@context": "http://schema.org",
  "@type": "WebSite",
  "url": `${getSiteUrl(forumType)}`,
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${combineUrls(getSiteUrl(forumType), '/search')}?query={search_term_string}`,
    "query-input": "required name=search_term_string"
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${getSiteUrl(forumType)}`,
  },
  ...(forumType === 'LessWrong' && {
    "description": [
      "LessWrong is an online forum and community dedicated to improving human reasoning and decision-making.", 
      "We seek to hold true beliefs and to be effective at accomplishing our goals.", 
      "Each day, we aim to be less wrong about the world than the day before."
    ].join(' ')
  }),
  ...(forumType === 'AlignmentForum' && {
    "description": [
      "The Alignment Forum is a single online hub for researchers to discuss all ideas related to ensuring that transformatively powerful AIs are aligned with human values.", 
      "Discussion ranges from technical models of agency to the strategic landscape, and everything in between."
    ].join(' ')
  }),
})

const LWHome = () => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const mobileSpotlightOverrideId = getMobileSpotlightOverrideId();
  const petrovDayRitualActive = useIsPetrovDayRitualActive();

  return (
    // Wait for spotlight selection and dismissal before revealing the posts
    // placeholder. The posts and spotlight content can then load independently.
    <SuspenseWrapper name="LWHome">
      <AnalyticsContext pageContext="homePage">
        <StructuredData generate={() => getStructuredData(forumType)}/>
        <UpdateLastVisitCookie />
        {reviewIsActive() && <>
          {getReviewPhase() !== "RESULTS" && <SingleColumnSection>
            <SuspenseWrapper name="FrontpageReviewWidget">
              <FrontpageReviewWidget reviewYear={REVIEW_YEAR}/>
            </SuspenseWrapper>
          </SingleColumnSection>}
        </>}
        {(!reviewIsActive() || getReviewPhase() === "RESULTS" || !showReviewOnFrontPageIfActive.get(forumType)) && !petrovDayRitualActive && <SingleColumnSection>
          <DismissibleSpotlightItemSuspense
            loadingStyle="placeholder"
            className={classes.desktopSpotlight}
          />
          <DismissibleSpotlightItemSuspense
            loadingStyle="placeholder"
            className={classes.mobileSpotlight}
            spotlightId={mobileSpotlightOverrideId}
          />
        </SingleColumnSection>}
        {petrovDayRitualActive && <PetrovStoryMobileBanner />}
        <SuspenseWrapper name="LWHomePosts" fallback={<div style={{height: 800}}/>}>
          <IsReturningVisitorContextProvider>
            <LWHomePosts>
              <SideItemsContainer>
                <div className={classes.feedSections}>
                  <div className={classes.sideItems}>
                    <SideItemsSidebar />
                  </div>
                  <QuickTakesSection />

                  <AnalyticsInViewTracker eventProps={{inViewType: "feedSection"}} observerProps={{threshold:[0, 0.5, 1]}}>
                    <SuspenseWrapper name="UltraFeed">
                      <ErrorBoundary fallback={<UltraFeedErrorFallback />}>
                        <UltraFeedOrRecentDiscussion/>
                      </ErrorBoundary>
                    </SuspenseWrapper>
                  </AnalyticsInViewTracker>
                </div>
              </SideItemsContainer>
            </LWHomePosts>
          </IsReturningVisitorContextProvider>
        </SuspenseWrapper>
      </AnalyticsContext>
    </SuspenseWrapper>
  )
}

const UltraFeedOrRecentDiscussion = () => {
  const { forumType } = useForumType();
  const ultraFeedEnabled = ultraFeedEnabledSetting.get(forumType)
  
  return ultraFeedEnabled
    ? <UltraFeed />
    : <DeferRender ssr={false}>
        <RecentDiscussionFeed
          af={false}
          commentsLimit={4}
          maxAgeHours={18}
        />
      </DeferRender>
}

const UpdateLastVisitCookie = () => {
  const { forumType } = useForumType();
  const [_, setCookie] = useCookiesWithConsent([LAST_VISITED_FRONTPAGE_COOKIE]);

  useEffect(() => {
    if (visitorGetsDynamicFrontpage(null, forumType)) {
      setCookie(LAST_VISITED_FRONTPAGE_COOKIE, new Date().toISOString(), { path: "/", expires: moment().add(1, 'year').toDate() });
    }
  }, [setCookie, forumType])

  return <></>
}

export default registerComponent('LWHome', LWHome, {
  areEqual: "auto",
});

